import { useState } from "react";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

async function post(url, body) {
  const opts = { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) };
  let res = await fetch(url, opts);
  if (res.status === 401) { await api.post("/auth/refresh").catch(() => {}); res = await fetch(url, opts); }
  return res;
}

export const AiHelp = ({ workspaceId, draftId, field }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const ask = async () => {
    setText(""); setError(""); setBusy(true);
    try {
      const res = await post(`${process.env.REACT_APP_BACKEND_URL}/api/workspaces/${workspaceId}/drafts/${draftId}/ai-help`, { field_key: field.key, question: q });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        return setError(typeof j.detail === "string" ? j.detail : "Please write a short question (at least 3 characters).");
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const ev of events) {
          const data = JSON.parse(ev.replace(/^data: /, ""));
          if (data.t) setText((t) => t + data.t);
          if (data.error) setError(data.error);
        }
      }
    } catch { setError("AI help is unavailable right now. The manual guidance is still here."); } finally { setBusy(false); }
  };

  if (!open) return (
    <button type="button" onClick={() => setOpen(true)} data-testid={`ai-help-open-${field.key}`} className="inline-flex items-center gap-1 text-xs font-medium text-navy underline">
      <Sparkles className="h-3.5 w-3.5 text-gold-dark" /> Ask AI about this question
    </button>
  );
  return (
    <div data-testid={`ai-help-panel-${field.key}`} className="space-y-2 rounded-md border border-gold/40 bg-white p-3">
      <Textarea value={q} onChange={(e) => setQ(e.target.value)} rows={2} maxLength={500} placeholder="What would you like explained?" data-testid={`ai-help-input-${field.key}`} />
      <p className="text-[11px] text-slate-500">Don't include personal numbers. AI only explains the question, can be wrong, and never fills in your answer.</p>
      <Button size="sm" onClick={ask} disabled={busy || q.trim().length < 3} data-testid={`ai-help-ask-${field.key}`} className="bg-navy hover:bg-navy-2">{busy ? "Thinking..." : "Ask"}</Button>
      {text && <div data-testid={`ai-help-answer-${field.key}`} className="whitespace-pre-wrap rounded bg-slate-50 p-2 text-xs text-slate-700"><p className="mb-1 font-semibold text-gold-dark">AI explanation: may be wrong. Check the official source.</p>{text}</div>}
      {error && <p data-testid={`ai-help-error-${field.key}`} className="text-xs text-red-700">{error}</p>}
    </div>
  );
};
